'use client';

import { createPalette, createTheme } from '@enlighten/design-system/dist/styles';

import { mobileMediaQuery } from '@/constants/common';

import { fonts } from './fonts';
import variables from './variables.module.scss';

export const palette = createPalette({
	common: {
		white: variables.white,
		black: variables.black,
	},
	primary: {
		main: variables.primary,
		light: variables.primaryLight,
	},
	secondary: {
		main: variables.secondary,
	},
	grey: {
		'100': variables.grey100,
		'200': variables.grey200,
		'300': variables.grey300,
		'400': variables.grey400,
		'500': variables.grey500,
		'600': variables.grey600,
		'700': variables.grey700,
		'800': variables.grey800,
		'900': variables.grey900,
		A200: variables.greyA200,
		A400: variables.greyA400,
		A700: variables.greyA700,
	},
	success: {
		main: variables.success,
	},
	error: {
		main: variables.error,
	},
});

export const theme = createTheme({
	palette,
	components: {
		MuiGrid: {
			styleOverrides: {
				root: {
					lineHeight: 0,
				},
			},
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: palette.grey['800'],
					opacity: 0.2,
				},
			},
		},
		MuiButton: {
			defaultProps: {
				variant: 'contained',
				size: 'medium',
				disableRipple: true,
			},
			styleOverrides: {
				root: {
					fontSize: 16,
					fontWeight: 700,
					lineHeight: 0,
					boxShadow: 'none',
					':hover': {
						boxShadow: 'none',
					},
					[mobileMediaQuery]: {
						fontSize: 14,
					},
				},
				outlined: {
					color: palette.grey['900'],
					bgcolor: 'transparent',
					borderColor: palette.grey['400'],
					':hover': {
						color: palette.primary.main,
						bgcolor: 'transparent',
					},
				},
				sizeSmall: {
					height: 44,
					paddingLeft: '15px',
					paddingRight: '15px',
				},
				sizeMedium: {
					height: 52,
					paddingLeft: '23px',
					paddingRight: '23px',
				},
				sizeLarge: {
					height: 56,
					paddingLeft: '31px',
					paddingRight: '31px',
				},

				containedPrimary: {
					':hover': {
						bgcolor: palette.primary.light,
					},
					'&.Mui-disabled': {
						color: palette.common.white,
						bgcolor: `${palette.primary.main}40`,
					},
				},
				containedSecondary: {
					':hover': {
						bgcolor: palette.secondary.main,
					},
				},
			},
		},
		MuiCheckbox: {
			defaultProps: {
				disableRipple: true,
			},
			styleOverrides: {
				root: {
					bgcolor: palette.common.white,
					padding: 0,
					stroke: palette.grey['400'],
					strokeWidth: 1,
				},
			},
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					fontWeight: 400,
					fontSize: 14,
					fontFamily: fonts.style.fontFamily,
					whiteSpace: 'pre-line',
					transition: 'all 0.3s ease',
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				root: {
					border: 'none',
					':hover': {
						border: 'none',
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					'&.Mui-error .MuiOutlinedInput-input': {
						border: `1px solid ${palette.error.main}`,
					},
				},
				input: {
					bgcolor: 'white',
					border: `1px solid ${palette.grey['400']}`,
					borderRadius: '6px',
					padding: '13px 11px',
					outline: 'none',
					transition: 'border 0.3s ease',
					':hover, :focus': {
						border: `1px solid ${palette.primary.light}`,
					},
				},
				notchedOutline: {
					display: 'none',
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontSize: 14,
					marginLeft: 0,
				},
			},
		},
		MuiSnackbar: {
			styleOverrides: {
				root: {
					'.MuiAlert-root': {
						bgcolor: palette.common.white,
						border: 'none',
						borderRadius: '6px',
						padding: '10 16px',
						boxShadow: '0 3px 6px -4px #0000001F, 0 6px 16px 0 #00000014, 0 9px 28px 8px #0000000D',
					},
				},
			},
		},
		MuiSkeleton: {
			defaultProps: {
				animation: 'wave',
			},
		},
	},
	typography: {
		fontSize: 14,
		htmlFontSize: 14,
		fontWeightLight: '100',
		fontWeightRegular: '400',
		fontWeightMedium: '500',
		fontWeightBold: '700',
		fontFamily: fonts.style.fontFamily,
		allVariants: {
			color: palette.grey['900'],
			letterSpacing: '-.05em',
			lineHeight: 1.23,
			whiteSpace: 'normal',
		},
	},
});
